<script setup lang="ts">
  import { whenever } from '@vueuse/core';
  import { computed, inject, ref } from 'vue';

  import { FilePurpose } from '@stuut/api/models';
  import type { Attachment, TaskRequirement } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { ButtonGroup } from '@ui/components/button-group';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import { FileDropzoneKey } from '@/components/file-upload-dropzone';
  import type { FileDropzoneProviderContext } from '@/components/file-upload-dropzone';

  const props = defineProps<{
    requirement: TaskRequirement;
  }>();

  const emit = defineEmits<{
    (e: 'resolve', value: string): void;
  }>();

  // Provider may be absent (closed-task render path) or disabled. Either case
  // collapses to a read-only display below — no throw, no upload UI.
  const dropzone = inject<FileDropzoneProviderContext>(FileDropzoneKey, undefined);

  const uploading = ref(false);
  const attachments = computed<Attachment[]>(() => dropzone?.fileAttachments.value ?? []);

  const savedValue = computed(() => {
    const value = props.requirement.resolution?.value;
    return typeof value === 'string' && value ? value : '';
  });

  const displayName = computed(() => attachments.value[0]?.name ?? savedValue.value);
  const displaySize = computed(() => attachments.value[0]?._file?.size ?? null);
  const hasFile = computed(() => !!displayName.value);

  const removeFile = () => {
    if (!dropzone) return;
    if (attachments.value.length) dropzone.fileAttachments.value.splice(0, 1);
    if (savedValue.value) emit('resolve', '');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const upload = async () => {
    const pending = attachments.value.filter(
      (a): a is Attachment & { _file: File } => !!a._file && !a.id,
    );
    if (!pending.length || uploading.value) return;

    uploading.value = true;

    try {
      const ids = await Promise.all(
        pending.map(async (attachment) => {
          const file = attachment._file;
          const { presignedUrl } = await stuut.files.create(file, {
            attachment,
            purpose: FilePurpose.CHAT,
          });
          await stuut.uploadAttachment(attachment, presignedUrl, file);
          return attachment.id;
        }),
      );

      emit('resolve', ids.join(','));
    } catch (err: any) {
      toast.error('File upload failed', { description: err?.message });
    } finally {
      uploading.value = false;
    }
  };

  whenever(
    () => attachments.value.length > 1,
    () => {
      if (!dropzone) return;
      dropzone.fileAttachments.value = attachments.value.slice(-1);
    },
  );

  whenever(() => attachments.value.some((a) => a._file && !a.id), upload, { immediate: true });
</script>

<template>
  <ButtonGroup class="border w-full">
    <template v-if="hasFile">
      <Button
        class="flex-1 bg-background flex items-center gap-x-2.5"
        variant="transparent"
        size="sm"
        :disabled="uploading"
        @click="dropzone?.openFileInput()"
      >
        <i
          class="far text-muted-foreground"
          :class="uploading ? 'fa-spinner-third fa-spin' : 'fa-paperclip'"
        />

        <span class="flex-1 truncate">{{ displayName }}</span>
        <span v-if="displaySize !== null" class="text-muted-foreground shrink-0">
          ({{ formatFileSize(displaySize) }})
        </span>
      </Button>

      <Button
        variant="transparent"
        size="icon-xs"
        class="bg-background h-8 text-muted-foreground items-center hover:text-destructive"
        :disabled="uploading"
        @click.stop="removeFile"
      >
        <i class="far fa-xmark" />
      </Button>
    </template>

    <template v-else>
      <Button
        class="flex-1 bg-background"
        variant="transparent"
        size="sm"
        @click="dropzone?.openFileInput()"
      >
        <i class="far fa-cloud-arrow-up text-muted-foreground" />
        <span>Choose a file or drag it here</span>
      </Button>
    </template>
  </ButtonGroup>
</template>
